import React from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { restartTour } from "@/components/ProductTour";
import {
  User, Ban, Calculator, CalendarDays, Sparkles, Utensils, ShoppingCart, FileText, Settings,
  Save, ChefHat, LineChart, Sunrise, Flag, Camera, Star, Zap, Refrigerator,
} from "lucide-react";

// User guide — kept in sync with the Feb 2026 launch build. Whenever a
// major feature ships, refresh the copy here so athletes see accurate
// walk-throughs from the header Help button.
const STEPS = [
  { icon: User, title: "1 · Athlete Profile",
    desc: "Week start, sex, age, height, weight, activity level, goal and training-energy support (Conservative / Balanced / Full). Flip units (kg/cm ↔ lb/in) any time from the top bar." },
  { icon: Settings, title: "2 · Setup",
    desc: "Four quick choices: how you'll use SPC (Full plan / Guidelines only / Tracker only), whether you want meal plans + a shopping list, your meal style (3 main meals, smaller meals + snacks, or Intermittent Fasting) and cooking style, plus TrainingPeaks linking. These decide which of the later steps you even see." },
  { icon: Ban, title: "3 · Dietary Requirements",
    desc: "Pick a dietary pattern, tap allergens or foods to exclude, add custom exclusions and list the sports products you already trust so we can safely include them." },
  { icon: Calculator, title: "4 · Macro & Target Engine",
    desc: "We calculate your BMR + baseline energy. Accept the auto macro split, or switch to Manual to fine-tune protein/carb/fat per kg." },
  { icon: CalendarDays, title: "5 · Training Week — with quick flags",
    desc: "Add up to 4 sessions per day (sport, duration, intensity, timing, terrain). Under every day's + Add button you'll see three tap-to-toggle icons: red Flag = Race, orange Flame = Heat, yellow Star = Key session. Bright when on, dim when off. Sync from TrainingPeaks pulls sessions straight in (Premium account needed for auto-sync)." },
  { icon: Sparkles, title: "6 · Your Week — Train & Fuel",
    desc: "Your plan builds itself as you go: daily energy and macro targets that scale with each day's training demand. If a Race session is flagged, the 3 days before automatically carb-load: +1 / +2 / +3 g/kg glycogen top-up on top of your normal training carbs." },
  { icon: Utensils, title: "7 · Meal Pathways + Batch Cook Sheet",
    desc: "Review two exclusion-safe pathways (A/B) for each day and tap the one you prefer. If your cooking style is Batch, you'll also see a Batch Cook Sheet up top — one CTA block per repeated recipe with total quantities to cook once, then per-day portion tiles (Mon 220g pasta, Wed 310g pasta…) and a fridge/freezer storage hint." },
  { icon: ShoppingCart, title: "8 · Shopping List — no waste, no guesswork",
    desc: "Consolidated weekly list grouped by aisle. Every item shows the days it's used (e.g. Mon · Wed or 5×/week). Perishables used only once get an orange Single-use warning + left border so you know to buy loose or the smallest pack. Copy or print in one tap." },
  { icon: FileText, title: "9 · Weekly Summary & Export",
    desc: "See the full week, name the plan by its start date, then Copy (for TrainingPeaks), Email or Print/PDF to take with you. Everything auto-saves as you go." },
];

export default function HelpDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { hasTracker } = useAuth();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="help-dialog"
        className="bg-[#0A0A0A] border-white/10 rounded-none text-white max-w-xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight text-white">How to use SPC Performance Nutrition</DialogTitle>
          <DialogDescription className="text-sm text-zinc-200 tracking-wide">
            Build a personalised weekly fuelling plan in 9 guided steps. Use <span className="text-[#E2E30A]">Next</span> / <span className="text-[#E2E30A]">Back</span> to move between them — your work auto-saves as you go.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2 overflow-y-auto flex-1 min-h-0 pr-1">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="flex gap-3 border border-white/10 bg-[#141414] p-3">
                <div className="grid place-items-center h-9 w-9 bg-[#E2E30A] shrink-0">
                  <Icon className="h-4 w-4 text-black" strokeWidth={2.2} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{s.title}</p>
                  <p className="text-xs text-zinc-200 mt-0.5 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            );
          })}

          <div className="flex gap-3 border border-[#FF3B30]/45 bg-[#FF3B30]/[0.07] p-3">
            <div className="grid place-items-center h-9 w-9 bg-[#FF3B30] shrink-0">
              <Flag className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Marking a race</p>
              <p className="text-xs text-zinc-200 mt-0.5 leading-relaxed">
                On the training calendar, tap the small red <b className="text-[#FF3B30]">Flag</b> icon under any day's + Add button (or open the session and toggle Race). That single tap sets the session's intensity to Race and the plan auto carb-loads the 3 days before. TrainingPeaks doesn't always sync a race as a race — this is the reliable way.
              </p>
            </div>
          </div>

          <div className="flex gap-3 border border-white/10 bg-[#141414] p-3">
            <div className="grid place-items-center h-9 w-9 bg-[#E2E30A] shrink-0">
              <Save className="h-4 w-4 text-black" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Instant Account Settings</p>
              <p className="text-xs text-zinc-200 mt-0.5 leading-relaxed">
                Open <span className="text-white">Profile → Account Settings</span> for one-tap edits that update the Tracker instantly — no plan resave needed:
              </p>
              <ul className="text-xs text-zinc-300 mt-1.5 leading-relaxed list-disc list-inside space-y-0.5">
                <li><span className="text-[#E2E30A]">Goal &amp; weight cadence</span> — daily / weekend / off for the Weight card</li>
                <li><span className="text-[#FB7185]">Progress Photos</span> — on by default; private (only you and your coach — not even admins can view)</li>
                <li><span className="text-zinc-100">Units</span> — flip between mi/km and kg/lb/stone everywhere</li>
              </ul>
            </div>
          </div>

          {hasTracker && (
            <div className="flex gap-3 border border-[#4C9AFF]/45 bg-[#4C9AFF]/[0.07] p-3">
              <div className="grid place-items-center h-9 w-9 bg-[#4C9AFF] shrink-0">
                <Utensils className="h-4 w-4 text-black" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">Food Tracker — Add Food dialog</p>
                <p className="text-xs text-zinc-200 mt-0.5 leading-relaxed">
                  Every logged meal has a <Star className="inline h-3 w-3 text-[#E2E30A]" /> star — tap it to save that food to Favourites. Opening + Log gives you 9 CTA tiles: My Plan · <b>Favourites</b> · <b>Recent (last 25)</b> · My Meals · Search · Scans · Barcode · Custom · Photo AI. Favourites + Recent re-log in one tap. Big + button, big meal picker pop-up — no fiddly dropdowns.
                </p>
                <Button data-testid="help-tracker-cta" onClick={() => { onOpenChange(false); navigate("/tracker"); }}
                  className="mt-2 h-9 rounded-none bg-[#4C9AFF] hover:bg-[#3a86e6] text-black text-xs font-bold uppercase tracking-wide transition-colors">
                  Open the Food Tracker
                </Button>
              </div>
            </div>
          )}

          {hasTracker && (
            <div className="flex gap-3 border border-[#FB7185]/45 bg-[#FB7185]/[0.07] p-3">
              <div className="grid place-items-center h-9 w-9 bg-[#FB7185] shrink-0">
                <Camera className="h-4 w-4 text-black" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Progress Photos — Before / After compare</p>
                <p className="text-xs text-zinc-200 mt-0.5 leading-relaxed">
                  Log body-progress shots inside Daily Snapshot with a pose tag (Front / Side / Back / Custom) so before / after always matches. Once you have 2+, tap <b>View timeline &amp; compare</b> — two big slots side-by-side, weight badge + 7-day average on each, plus a delta banner ("−2.4 kg · 26 days apart"). Private by design.
                </p>
              </div>
            </div>
          )}

          {hasTracker && (
            <div className="flex gap-3 border border-[#34C759]/45 bg-[#34C759]/[0.07] p-3">
              <div className="grid place-items-center h-9 w-9 bg-[#34C759] shrink-0">
                <LineChart className="h-4 w-4 text-black" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">Insights</p>
                <p className="text-xs text-zinc-200 mt-0.5 leading-relaxed">
                  Long-term trends across weight, calories, adherence and food quality (5-a-day, protein sources, processed food, meat vs plant). Switch 7d / 30d / 90d / 1yr / 2yr / All-time.
                </p>
                <Button data-testid="help-insights-cta" onClick={() => { onOpenChange(false); navigate("/insights"); }}
                  className="mt-2 h-9 rounded-none bg-[#34C759] hover:bg-[#2aab4a] text-black text-xs font-bold uppercase tracking-wide transition-colors">
                  Open Insights
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-3 border border-[#FF9500]/45 bg-[#FF9500]/[0.07] p-3">
            <div className="grid place-items-center h-9 w-9 bg-[#FF9500] shrink-0">
              <ChefHat className="h-4 w-4 text-black" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">Recipes &amp; Nutrition Guide</p>
              <p className="text-xs text-zinc-200 mt-0.5 leading-relaxed">
                <span className="text-white">Recipes</span> — athlete meals &amp; snacks (macros, ingredients, method) filtered by meal or dietary fit. <span className="text-white">Guide</span> — plain-English answers on fuelling, macros, supplements, race prep.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <Button data-testid="help-recipes-cta" onClick={() => { onOpenChange(false); navigate("/recipes"); }}
                  className="h-9 rounded-none bg-[#FF9500] hover:bg-[#e08600] text-black text-xs font-bold uppercase tracking-wide transition-colors">
                  Browse Recipes
                </Button>
                <Button data-testid="help-guide-cta" onClick={() => { onOpenChange(false); navigate("/learn"); }}
                  className="h-9 rounded-none bg-transparent border border-[#34C759]/60 text-[#34C759] hover:bg-[#34C759]/10 text-xs font-bold uppercase tracking-wide transition-colors">
                  Open Guide
                </Button>
              </div>
            </div>
          </div>

          <div className="flex gap-3 border border-white/10 bg-[#141414] p-3">
            <div className="grid place-items-center h-9 w-9 bg-white shrink-0">
              <Zap className="h-4 w-4 text-black" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Saving &amp; switching plans</p>
              <p className="text-xs text-zinc-200 mt-0.5 leading-relaxed">
                Everything auto-saves (watch the Saved indicator). Open <span className="text-white">My Plans</span> from Home to start a new week, duplicate this one, or reopen a saved plan.
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-3 shrink-0">
          <Button data-testid="help-tour-btn" onClick={() => { onOpenChange(false); restartTour(); }}
            className="h-11 px-4 rounded-none bg-transparent border border-white/20 text-zinc-200 hover:bg-white/5 text-xs font-bold uppercase tracking-wide transition-colors">
            Quick tour
          </Button>
          <Button data-testid="help-got-it-btn" onClick={() => onOpenChange(false)}
            className="flex-1 h-11 rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-semibold uppercase tracking-wide transition-colors">
            Got it — let's build my plan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
